import { http } from "../helpers/easyindustriaapi/config";

export default class AccountsPayable {
    constructor(user = null, empresa = null) {
        this.http = http;
        this.user = user;
        this.empresa = empresa;
    }

    /**
     * Busca todas as contas a pagar da empresa
     * @param {number} empresaId - ID da empresa
     * @returns {Promise} Response com contasPagar e planoContas
     */
    async getAccountsPayable(empresaId = this.empresa.id) {
        try {
            const response = await this.http.get(`/contas-a-pagar?empresa_id=${empresaId}`);
            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                status: error.response?.status || 500
            };
        }
    }

    /**
     * Cria uma nova conta a pagar
     * @param {Object} contaPagar - Dados da conta a pagar
     * @param {number} empresaId - ID da empresa
     * @returns {Promise} Response da criação
     */
    async createAccountPayable(contaPagar, empresaId = this.empresa.id) {
        try {
            const response = await this.http.post(`/contas-a-pagar?empresa_id=${empresaId}`, contaPagar);
            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                message: error.response?.data?.message || 'Erro ao incluir conta a pagar',
                status: error.response?.status || 500
            };
        }
    }

    /**
     * Atualiza uma conta paga
     * @param {Object} contaPaga - Dados da conta paga
     * @param {number} empresaId - ID da empresa
     * @returns {Promise} Response da atualização
     */
    async updateAccountPaid(contaPagar, empresaId = this.empresa.id) {
        try {
            const response = await this.http.put(`/contas-a-pagar/${contaPagar.id}?empresa_id=${empresaId}`, contaPagar);
            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                status: error.response?.status || 500
            };
        }
    }

    /**
     * Exclui uma conta paga
     * @param {Object} contaPaga - Dados da conta paga
     * @param {number} empresaId - ID da empresa
     * @returns {Promise} Response da exclusão
     */
    async deleteAccountPaid(contaPagar, empresaId = this.empresa.id) {
        try {
            const response = await this.http.delete(`/contas-a-pagar/${contaPagar.id}?empresa_id=${empresaId}`, contaPagar);
            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                status: error.response?.status || 500
            };
        }
    }

    /**
     * Busca tipos de pagamento da empresa
     * @param {number} empresaId - ID da empresa
     * @returns {Promise} Response com tipos de pagamento
     */
    async getPaymentTypes(empresaId = this.empresa.id) {
        try {
            const response = await this.http.get(`/tipopagamento?empresa_id=${empresaId}`);
            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                status: error.response?.status || 500
            };
        }
    }

    /**
     * Busca históricos padrão da empresa
     * @param {number} empresaId - ID da empresa
     * @returns {Promise} Response com históricos
     */
    async getDefaultHistories(empresaId = this.empresa.id) {
        try {
            const response = await this.http.get(`/historicopadrao?empresa_id=${empresaId}`);
            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                status: error.response?.status || 500
            };
        }
    }

    /**
     * Busca fornecedores/pessoas da empresa
     * @param {number} empresaId - ID da empresa
     * @returns {Promise} Response com fornecedores
     */
    async getSuppliers(empresaId = this.empresa.id) {
        try {
            const response = await this.http.get(`/pessoa?empresa_id=${empresaId}`);
            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                status: error.response?.status || 500
            };
        }
    }

    /**
     * Busca centros de custo da empresa
     * @param {number} empresaId - ID da empresa
     * @returns {Promise} Response com centros de custo
     */
    async getCostCenters(empresaId = this.empresa.id) {
        try {
            const response = await this.http.get(`/centrocusto?empresa_id=${empresaId}`);
            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                status: error.response?.status || 500
            };
        }
    }

    /**
     * Busca centros de resultado da empresa
     * @param {number} empresaId - ID da empresa
     * @returns {Promise} Response com centros de resultado
     */
    async getResultCenters(empresaId = this.empresa.id) {
        try {
            const response = await this.http.get(`/centroresultado?empresa_id=${empresaId}`);
            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                status: error.response?.status || 500
            };
        }
    }

    /**
     * Paga múltiplas contas a pagar de uma vez (pagamento em massa)
     * @param {Array} contasIds - Array com IDs das contas a pagar
     * @param {number} empresaId - ID da empresa
     * @param {number} userId - ID do usuário
     * @returns {Promise} Response do pagamento em massa
     */
    async payMultipleAccounts(contasIds, empresaId = this.empresa.id, userId = this.user.id) {
        try {
            const response = await this.http.put(`/contas-a-pagar/pagar-multiplas?empresa_id=${empresaId}`, {
                contas_ids: contasIds,
                user_id: userId,
                data_pagamento: new Date().toISOString().split('T')[0] // Data atual
            });
            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                message: error.response?.data?.message || 'Erro ao pagar contas selecionadas',
                status: error.response?.status || 500
            };
        }
    }

    /**
     * Busca contas a pagar por vencimento
     * @param {number} empresaId - ID da empresa
     * @returns {Promise} Response com contas a pagar por vencimento
     */
    async getAccountsPayableDueDate(empresaId = this.empresa.id) {
        try {
            const response = await this.http.get(`/contas-a-pagar/por-vencimento?empresa_id=${empresaId}`);
            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                status: error.response?.status || 500
            };
        }
    }

    /**
     * Cancela baixa de uma conta individual
     * @param {Object} contaPagar - Dados da conta a pagar
     * @param {number} empresaId - ID da empresa
     * @returns {Promise} Response do cancelamento
     */
    async cancelarBaixa(contaPagar, empresaId = this.empresa.id) {
        try {
            const response = await this.http.post(`/contas-a-pagar/cancelar-baixa?empresa_id=${empresaId}`, {
                id: contaPagar.id
            });
            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                message: error.response?.data?.message || 'Erro ao cancelar baixa',
                status: error.response?.status || 500
            };
        }
    }

    /**
     * Cancela baixa de múltiplas contas
     * @param {Array} contasIds - Array com IDs das contas
     * @param {number} empresaId - ID da empresa
     * @returns {Promise} Response do cancelamento em massa
     */
    async cancelarBaixasMultiplas(contasIds, empresaId = this.empresa.id) {
        try {
            const response = await this.http.post(`/contas-a-pagar/cancelar-baixas-multiplas?empresa_id=${empresaId}`, {
                contas_ids: contasIds
            });
            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                message: error.response?.data?.message || 'Erro ao cancelar baixas',
                status: error.response?.status || 500
            };
        }
    }

    /**
     * Exclui múltiplas contas
     * @param {Array} contasIds - Array com IDs das contas
     * @param {number} empresaId - ID da empresa
     * @returns {Promise} Response da exclusão em massa
     */
    async excluirMultiplas(contasIds, empresaId) {
        try {
            const response = await this.http.post(`/contas-a-pagar/excluir-multiplas?empresa_id=${empresaId}`, {
                contas_ids: contasIds
            });
            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                message: error.response?.data?.message || 'Erro ao excluir contas',
                status: error.response?.status || 500
            };
        }
    }

    /**
     * Baixa uma conta individual
     * @param {Object} contaPagar - Dados da conta a pagar
     * @param {number} empresaId - ID da empresa
     * @returns {Promise} Response da baixa
     */
    async baixarConta(contaPagar, empresaId) {
        try {
            const response = await this.http.post(`/contas-a-pagar/baixar-conta?empresa_id=${empresaId}`, {
                id: contaPagar.id,
                data_pagamento: new Date().toISOString().split('T')[0] // Data atual
            });
            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                message: error.response?.data?.message || 'Erro ao baixar conta',
                status: error.response?.status || 500
            };
        }
    }

    /**
     * Upload de arquivo e vínculo com Conta a Pagar
     * @param {number} contaPagarId
     * @param {File} file
     * @param {number} empresaId
     */
    async uploadArquivo(contaPagarId, file, empresaId) {
        try {
            const form = new FormData();
            form.append('file', file);
            if (empresaId !== undefined && empresaId !== null && empresaId !== '') {
                form.append('empresa_id', String(empresaId));
            }

            const response = await this.http.post(
                `/contas-a-pagar/${contaPagarId}/upload`,
                form
            );

            const payload = response?.data || {};
            const successFlag = payload.success === true;

            return {
                success: successFlag,
                data: payload.arquivo || payload.data || null,
                message: payload.message || (successFlag ? 'Arquivo enviado com sucesso.' : 'Falha ao enviar arquivo.'),
                status: response.status
            };
        } catch (error) {
            const response = error.response || {};
            return {
                success: false,
                error: response || error,
                message: response.data?.message || response.data?.error || 'Erro ao enviar arquivo',
                status: response.status || 500
            };
        }
    }

    async deleteArquivo(contaPagarId, arquivoId, empresaId) {
        try {
            const response = await this.http.delete(
                `/contas-a-pagar/${contaPagarId}/arquivos/${arquivoId}?empresa_id=${empresaId}`
            );

            const payload = response?.data || {};
            const successFlag = payload.success === undefined ? true : payload.success === true;

            return {
                success: successFlag,
                message: payload.message || (successFlag ? 'Arquivo excluído com sucesso.' : 'Falha ao excluir arquivo.'),
                status: response.status
            };
        } catch (error) {
            const response = error.response || {};
            return {
                success: false,
                error: response || error,
                message: response.data?.message || response.data?.error || 'Erro ao excluir arquivo',
                status: response.status || 500
            };
        }
    }

    /**
     * Busca as evoluções (recurrences) vinculadas à conta_pagar_id.
     * @param {number} contaPagarId
     * @param {number} empresaId
     */
    async getEvolucoesByContaPagar(contaPagarId, empresaId = this.empresa.id) {
        try {
            const response = await this.http.get(
                `/contas-a-pagar/${contaPagarId}/evolucoes?empresa_id=${empresaId}`
            );

            return {
                success: true,
                data: response.data,
                status: response.status
            };
        } catch (error) {
            return {
                success: false,
                error: error.response || error,
                message: error.response?.data?.message || error.response?.data?.error || 'Erro ao carregar evoluções',
                status: error.response?.status || 500
            };
        }
    }
}